import { motion } from "framer-motion";
import { Copy, ExternalLink, Send } from "lucide-react";
import WhaleLogo from "./WhaleLogo";
import { copyCa } from "./Navbar";
import { CA, BUY_URL, TELEGRAM_URL, X_URL } from "../lib/constants";

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93Zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41Z" />
  </svg>
);

export default function CaFooter({ onNav }) {
  return (
    <footer data-testid="footer-section" className="relative bg-ocean-bg2">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-8">
        <motion.div
          initial={{ y: 36, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          className="sticker-lg relative overflow-hidden rounded-[2rem] bg-ocean-yellow p-8 text-center sm:p-14"
        >
          <WhaleLogo className="absolute -left-8 -top-8 h-32 w-32 opacity-15" flip />
          <WhaleLogo className="absolute -bottom-8 -right-8 h-32 w-32 opacity-15" />
          <p className="text-xs font-extrabold uppercase tracking-[0.3em]">
            The pod's home address
          </p>
          <h2 className="mt-3 font-display text-4xl font-semibold tracking-tight sm:text-6xl">
            COPY THE CA
          </h2>
          <p
            data-testid="footer-ca-text"
            className="mx-auto mt-6 max-w-3xl break-all rounded-2xl border-[3px] border-ocean-navy bg-white/80 px-4 py-4 font-mono text-sm font-bold sm:px-6 sm:text-lg"
          >
            {CA}
          </p>
          <button
            data-testid="footer-ca-copy"
            onClick={copyCa}
            className="btn-push sticker mx-auto mt-7 flex items-center gap-2.5 rounded-2xl bg-ocean-navy px-9 py-4 font-display text-xl font-semibold text-white"
          >
            <Copy className="h-5 w-5" />
            Copy CA
          </button>
        </motion.div>

        <div className="mt-16 flex flex-col items-center justify-between gap-8 pb-4 md:flex-row md:items-start">
          <div className="flex flex-col items-center gap-3 md:items-start">
            <div className="flex items-center gap-2.5">
              <WhaleLogo className="h-10 w-10" />
              <span className="font-display text-2xl font-semibold tracking-tight">
                BLUEWHALE
              </span>
            </div>
            <p className="max-w-xs text-center text-sm font-medium text-ocean-muted md:text-left">
              $BLUE — Big waves. Bigger community. Live on Solana.
            </p>
          </div>

          <nav className="flex flex-wrap items-center justify-center gap-5">
            {[
              { id: "story", label: "Story" },
              { id: "chart", label: "Chart" },
              { id: "tokenomics", label: "Tokenomics" },
              { id: "how-to-buy", label: "How to Buy" },
            ].map((l) => (
              <button
                key={l.id}
                data-testid={`footer-nav-link-${l.id}`}
                onClick={() => onNav(l.id)}
                className="text-sm font-bold text-ocean-muted transition-colors hover:text-ocean-navy"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="footer-telegram-link"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-push sticker-sm rounded-2xl bg-ocean-sky p-3"
              aria-label="Telegram"
            >
              <Send className="h-5 w-5" />
            </a>
            <a
              data-testid="footer-x-link"
              href={X_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-push sticker-sm rounded-2xl bg-white p-3"
              aria-label="X (Twitter)"
            >
              <XIcon className="h-5 w-5" />
            </a>
            <a
              data-testid="footer-dexscreener-link"
              href={BUY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-push sticker-sm flex items-center gap-1.5 rounded-2xl bg-ocean-yellow px-4 py-3 font-display text-sm font-semibold"
            >
              DexScreener
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div className="mt-10 border-t-[3px] border-ocean-navy/10 pt-6 text-center">
          <p className="text-xs font-medium leading-relaxed text-ocean-muted">
            $BLUE is a memecoin created for fun and community. It has no
            utility, no intrinsic value and no expectation of financial return.
            Nothing here is financial advice. Ride the waves at your own risk.
          </p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-ocean-navy/60">
            © Bluewhale. All waves reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
